import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import type { SubscriptionRow } from 'src/app/core/services/subscription-api.service';
import { ActionMenuPanelDirective } from 'src/app/shared/directives/action-menu-panel.directive';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';

export type SubscriptionListView = 'active' | 'deleted';

@Component({
  selector: 'app-subscription-cards',
  standalone: true,
  imports: [
    CommonModule,
    IconModule,
    SharedModule,
    HasPermissionPipe,
    ActionMenuPanelDirective,
    AppDatePipe,
  ],
  templateUrl: './subscription-cards.component.html',
})
export class SubscriptionCardsComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: SubscriptionRow[] = [];
  @Input() view: SubscriptionListView = 'active';
  @Input() loading = false;
  @Input() emptyMessage = 'No subscriptions found.';
  @Input() openActionMenuKey: string | null = null;

  @Output() toggleMenu = new EventEmitter<{ key: string; event: MouseEvent }>();
  @Output() closeMenu = new EventEmitter<void>();
  @Output() edit = new EventEmitter<SubscriptionRow>();
  @Output() delete = new EventEmitter<SubscriptionRow>();
  @Output() permanentDelete = new EventEmitter<SubscriptionRow>();
  @Output() restore = new EventEmitter<SubscriptionRow>();

  actionMenuRowKey(index: number): string {
    return `sub-${index}`;
  }

  formatPrice(value: number | string | null | undefined): string {
    if (value == null || value === '') return '—';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return String(value);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(n);
  }

  tenantLabels(row: SubscriptionRow): string[] {
    const links = row.subscriptionTenants ?? [];
    if (!links.length) return [];
    return links.map((link) => {
      if (!link.tenant) return `Client #${link.tenant_id}`;
      return (
        link.tenant.tenant_name ??
        link.tenant.tenant_subdomain ??
        `Client #${link.tenant_id}`
      );
    });
  }

  typeBadgeClass(type: string | null | undefined): string {
    const t = (type ?? '').toUpperCase();
    if (t === 'CUSTOM') {
      return 'bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200';
    }
    return 'bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200';
  }

  statusBadgeClass(row: SubscriptionRow): string {
    if (this.view === 'deleted') {
      return 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200';
    }
    return row.is_active
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
      : 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300';
  }

  statusLabel(row: SubscriptionRow): string {
    if (this.view === 'deleted') return 'Deleted';
    return row.is_active ? 'Active' : 'Inactive';
  }

  modelQuotaEntries(
    row: SubscriptionRow,
  ): Array<{ label: string; count: number }> {
    const stored = row.no_of_subscription_models;
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
      return [];
    }
    const out: Array<{ label: string; count: number }> = [];
    for (const [key, value] of Object.entries(stored)) {
      const entry = this.toQuotaEntry(key, value);
      if (entry) out.push(entry);
    }
    return out;
  }

  private toQuotaEntry(
    key: string,
    value: unknown,
  ): { label: string; count: number } | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return { label: key, count: value };
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    const obj = value as { product_name?: unknown; no_of_models?: unknown };
    const count = Number(obj.no_of_models);
    if (!Number.isFinite(count)) return null;
    const label =
      typeof obj.product_name === 'string' && obj.product_name.trim()
        ? obj.product_name
        : key;
    return { label, count };
  }
}
